import {computed, ComputedRef, ref, Ref} from "vue";
import Customer from "@/customer/interfaces/Customer";
import {CustomerProfile} from "@/drug-interaction/components/DrugInteractionPatientProfile.vue";
import {CustomerDeclaredInformation} from "@/customer/interfaces/DeclaredInformation";
import {VidalAllergy, VidalPathology} from "@/customer/interfaces/Patient";

type Patch = (
    customerId: number,
    declaredInformation: Partial<CustomerDeclaredInformation>,
    allergies?: VidalAllergy[],
    pathologies?: VidalPathology[]
) => Promise<void>;

const usePatientProfile = (
    customer: ComputedRef<Customer | null> | Ref<Customer | null>,
    patch: Patch
) => {
    const saving = ref<boolean>(false);

    const profile = computed(() => {
        if (!customer.value) {
            return {} as CustomerProfile;
        }

        return {
            ...(customer.value.declared_information ?? {}),
            pathologies: customer.value.vidal_pathologies ?? [],
            allergies: customer.value.vidal_allergies ?? []
        } as CustomerProfile;
    });

    const save = async (data: Omit<CustomerProfile, "id" | "is_dead">) => {
        const information: Partial<CustomerDeclaredInformation> = {};

        const {pathologies, allergies, ...declaredInformation} = data;

        Object.keys(declaredInformation).forEach(key => {
            information[key] = declaredInformation[key];
        });

        saving.value = true;

        return patch(customer.value!.id, information, allergies, pathologies)
            .finally(() => saving.value = false);
    };

    return {
        profile,
        saving,
        save
    };
};

export default usePatientProfile;
